import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './BaseTool.css';
import Navbar from '../Page/Navbar';
import Footer from '../Page/Footer';
import api from '../api';
import RatingComponent from './RatingComponent';
import useStaticRatings from './useStaticRatings';

const ExpandTool = () => {
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [targetLength, setTargetLength] = useState('double');

    // Tool ID for rating component
    const toolId = 'expand';
    
    // Use static ratings for schema.org (loads immediately)
    const staticRatings = useStaticRatings(toolId);

    // Schema.org will use static ratings (available immediately)

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "AI Text Expansion Tool",
        "headline": "Expand Your Text with AI – Make Content Longer & More Detailed",
        "description": "Use our free AI-powered tool to expand your text intelligently. Add structure, examples, and explanations while keeping your message clear.",
        "url": "https://bookwitch.io/tools/expand",
        "applicationCategory": "WritingApplication",
        "operatingSystem": "Web browser",
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
        },
        "featureList": "Context-aware generation, Flexible length control, Polished results",
        "screenshot": "https://bookwitch.io/images/expand-tool-screenshot.jpg",
        "creator": {
            "@type": "Organization",
            "name": "Book Witch",
            "url": "https://bookwitch.io"
        },
        "potentialAction": {
            "@type": "UseAction",
            "target": "https://bookwitch.io/tools/expand"
        },
        "aggregateRating": staticRatings.hasValidRatings ? {
            "@type": "AggregateRating",
            "ratingValue": staticRatings.average.toFixed(1),
            "ratingCount": staticRatings.count,
            "bestRating": "5",
            "worstRating": "1"
        } : undefined
    };

    // Remove undefined fields from jsonLd
    Object.keys(jsonLd).forEach(key => {
        if (jsonLd[key] === undefined) {
            delete jsonLd[key];
        }
    });

    // Schema is ready when static ratings are loaded (immediate or after fetch)
    const shouldRenderSchema = staticRatings.isLoaded;

    const handleProcess = async () => {
        if (!inputText.trim()) return;

        setIsLoading(true);
        try {
            const response = await api.post('/tools/expand', {
                content: inputText,
                target_length: targetLength
            });
            setOutputText(response.data.modified_content);
        } catch (error) {
            console.error('Expand error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const lengthOptions = [
        { value: 'double', label: '2x Longer' },
        { value: 'triple', label: '3x Longer' },
        { value: 'quadruple', label: '4x Longer' }
    ];

    return (
        <>
            <Helmet>
                <title>Expand Your Text with AI – Make Content Longer & More Detailed</title>
                <meta name="description" content="Use our free AI-powered tool to expand your text intelligently. Add structure, examples, and explanations while keeping your message clear." />


                <meta name="keywords" content="content expansion tool, text expander, AI writing tool, content lengthener, article expander, text expansion tool, content development tool" />

                <meta property="og:type" content="website" />
                <meta property="og:title" content="Expand Your Content with AI – Add Details and Depth Instantly | Book Witch" />
                <meta property="og:description" content="Make your content longer and more detailed with our AI expansion tool. Add relevant examples, details, and explanations while keeping it coherent and clear." />
                <meta property="og:url" content="https://bookwitch.io/tools/expand" />

                <link rel="canonical" href="https://bookwitch.io/tools/expand" />
                {shouldRenderSchema && (
                    <script type="application/ld+json">
                        {JSON.stringify(jsonLd)}
                    </script>
                )}
            </Helmet>

            <Navbar />
            <div className="base-tool">
                <div className="bt-header">
                    <h1>Expand Your Content with AI – Add Details and Depth Instantly</h1>
                    <p>Struggling with short or underdeveloped text? Our AI content expansion tool helps you make your writing longer, more detailed, and more complete — all while keeping it clear and coherent.</p>
                </div>

                <div className="bt-features">
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">psychology</span>
                        <h3>Context-aware generation</h3>
                        <p>The tool adds examples, details, and explanations that naturally fit your text</p>
                    </div>
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">settings</span>
                        <h3>Flexible length control</h3>
                        <p>Choose how much longer you want your content to be (2x, 3x, or more)</p>
                    </div>
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">auto_awesome</span>
                        <h3>Polished results</h3>
                        <p>Output is well-structured and ready to use in your blog, paper, or ebook</p>
                    </div>
                </div>

                <div className="bt-editor">
                    <textarea
                        className="textarea"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder="Enter your text here..."
                    />
                    <div className="bt-controls">
                        <select
                            value={targetLength}
                            onChange={(e) => setTargetLength(e.target.value)}
                        >
                            {lengthOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={handleProcess}
                            disabled={isLoading || !inputText.trim()}
                            className="button-important"
                        >
                            <div className="inner">
                                {isLoading ? 'Processing...' : 'Expand'}
                            </div>
                        </button>
                    </div>
                </div>

                {outputText && (
                    <div className="bt-output">
                        <textarea
                            className="textarea"
                            value={outputText}
                            readOnly
                        />
                        <button
                            onClick={() => navigator.clipboard.writeText(outputText)}
                            className="button-hollow"
                        >
                            <span className="material-symbols-outlined">content_copy</span>
                            Copy
                        </button>
                    </div>
                )}

                {/* Rating Component */}
                <RatingComponent toolId={toolId} />

                <section className="bt-section">
                    <div className="bt-section-header">
                        <h2>How it works</h2>
                    </div>

                    <div className="bt-use-cases-grid">
                        <div className="bt-use-case-card">
                            <h3>1. Paste your text</h3>
                            <p>Paste your text into the editor.</p>
                        </div>
                        <div className="bt-use-case-card">
                            <h3>2. Choose length</h3>
                            <p>Choose how much you want to expand it.</p>
                        </div>
                        <div className="bt-use-case-card">
                            <h3>3. See results</h3>
                            <p>Click <strong>Expand</strong> and let the AI work its magic.</p>
                        </div>
                    </div>
                </section>

                <section className="bt-cta">
                    <h2>Want to create full documents?</h2>
                    <p>You can use the expanded version as the base for a full document in Book Witch — our AI tool for creating ebooks, lead magnets, and more.</p>
                    <p>Start with a paragraph. End with a book.</p>
                    <div className="bt-cta-buttons">
                        <Link to="/app" className="no-style-link">
                            <button className="button-important">
                                <div className="inner">
                                    Try Book Witch
                                    <span className="material-symbols-outlined">arrow_forward</span>
                                </div>
                            </button>
                        </Link>
                    </div>
                </section>

                <section className="bt-faq">
                    <h2>FAQ – AI Content Expansion</h2>
                    <div className="bt-faq-item">
                        <h3>Does expanded content sound natural?</h3>
                        <p>Yes, our AI is trained to add content that flows naturally with your existing text, maintaining your tone and style while adding relevant information.</p>
                    </div>
                    <div className="bt-faq-item">
                        <h3>What kind of content works best with this tool?</h3>
                        <p>The expansion tool works with virtually any type of content, but performs best on factual, informative, or educational texts where details, examples, and explanations can be added.</p>
                    </div>
                    <div className="bt-faq-item">
                        <h3>Will the expanded content be accurate?</h3>
                        <p>The AI expands based on general knowledge and patterns learned during training. For highly specialized or technical content, we recommend reviewing the expanded text for accuracy.</p>
                    </div>
                </section>
            </div>
            <Footer />
        </>
    );
};

export default ExpandTool; 